import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  Collapse,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  AccountBalance, CreditCard, Savings, AccountBalanceWallet, Close, AddCircleOutline, Sync,
  Home, Add, ExpandLess, ExpandMore, Edit as EditIcon, Delete,
} from '@mui/icons-material';
import { usePlaidLink } from 'react-plaid-link';
import { plaidService, Account, Transaction, ManualAccount } from '../services/plaidService';
import {
  cachedAccounts,
  cachedManualAccounts,
  cachedLinkToken,
  cachedTransactions,
  invalidateAccounts,
  invalidateAllData,
} from '../services/prefetch';

const Accounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [accountTransactions, setAccountTransactions] = useState<Transaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [displayedTransactions, setDisplayedTransactions] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  
  // Plaid Link states
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState(false);

  // Manual real estate property states
  const [manualAccounts, setManualAccounts] = useState<ManualAccount[]>([]);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [propertyName, setPropertyName] = useState('');
  const [propertyType, setPropertyType] = useState('primary_residence');
  const [propertyValue, setPropertyValue] = useState('');
  const [propertyCurrency, setPropertyCurrency] = useState('USD');
  const [addingProperty, setAddingProperty] = useState(false);
  const [editingProperty, setEditingProperty] = useState<ManualAccount | null>(null);
  const [editName, setEditName] = useState('');
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const getAccountType = (accountId: string) => {
    const account = accounts.find(acc => acc.account_id === accountId);
    return account ? account.type : 'unknown';
  };

  const getDisplayAmount = (transaction: Transaction) => {
    const accountType = getAccountType(transaction.account_id);
    const originalAmount = transaction.amount;
    
    // According to Plaid docs:
    // - Positive values = money moves OUT of account (spending)
    // - Negative values = money moves IN to account (income/payments)
    
    if (accountType === 'credit') {
      // For credit cards: invert the amount to show user-friendly view
      // Plaid positive = purchase (spending) = show as negative
      // Plaid negative = payment (debt reduction) = show as positive
      return -originalAmount;
    }
    
    // For depository accounts (checking, savings):
    // Plaid positive = money out (spending) = show as negative
    // Plaid negative = money in (income) = show as positive
    return -originalAmount;
  };

  useEffect(() => {
    fetchAccounts();
    createLinkToken();
    fetchManualAccounts();
  }, []);

  const createLinkToken = async () => {
    try {
      const response = await cachedLinkToken();
      setLinkToken(response.link_token);
    } catch (err) {
      console.error('Error creating link token:', err);
    }
  };

  const onPlaidSuccess = async (publicToken: string, metadata: any) => {
    try {
      setLinkLoading(true);
      setLinkError(null);
      const response = await plaidService.exchangeToken(publicToken);

      if (response.access_token) {
        localStorage.setItem('access_token', response.access_token);
        setLinkSuccess(true);
        // New account linked → drop cached data so all pages refetch fresh
        invalidateAllData();
        await fetchAccounts();
        setTimeout(() => {
          setLinkSuccess(false);
        }, 3000);
      }
    } catch (err) {
      setLinkError('Failed to link account. Please try again.');
      console.error('Error exchanging token:', err);
    } finally {
      setLinkLoading(false);
    }
  };

  const onPlaidExit = (err: any, metadata: any) => {
    if (err) {
      setLinkError('Account linking was cancelled or failed.');
      console.error('Plaid Link error:', err);
    }
  };

  const plaidConfig = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
  };

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink(plaidConfig);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await cachedAccounts();
      setAccounts(response.accounts);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchManualAccounts = async () => {
    try {
      const accounts = await cachedManualAccounts();
      setManualAccounts(accounts);
    } catch (error) {
      console.error('Error fetching manual accounts:', error);
    }
  };

  const formatPropertyCurrency = (value: number) =>
    `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })}`;

  const handleAddProperty = async () => {
    if (!propertyName || !propertyValue) return;
    setAddingProperty(true);
    try {
      await plaidService.createManualAccount({
        name: propertyName,
        type: 'real_estate',
        subtype: propertyType,
        current_balance: parseFloat(propertyValue),
        iso_currency_code: propertyCurrency,
      });
      setPropertyName('');
      setPropertyType('primary_residence');
      setPropertyValue('');
      setPropertyCurrency('USD');
      setShowAddProperty(false);
      invalidateAccounts();
      await fetchManualAccounts();
      await fetchAccounts();
    } catch (error) {
      console.error('Error adding property:', error);
    } finally {
      setAddingProperty(false);
    }
  };

  const handleEditProperty = (property: ManualAccount) => {
    setEditingProperty(property);
    setEditName(property.name);
    setEditValue(String(property.current_balance));
  };

  const handleSaveEdit = async () => {
    if (!editingProperty || !editName || !editValue) return;
    setSavingEdit(true);
    try {
      await plaidService.updateManualAccount(editingProperty.id, {
        name: editName,
        current_balance: parseFloat(editValue),
      });
      setEditingProperty(null);
      invalidateAccounts();
      await fetchManualAccounts();
      await fetchAccounts();
    } catch (error) {
      console.error('Error updating property:', error);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteProperty = async (id: number) => {
    setDeletingId(id);
    try {
      await plaidService.deleteManualAccount(id);
      invalidateAccounts();
      await fetchManualAccounts();
      await fetchAccounts();
    } catch (error) {
      console.error('Error deleting property:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSyncData = async () => {
    setSyncing(true);
    try {
      const result = await plaidService.syncData();
      setSyncMessage(`${result.message}. Synced ${result.accounts_synced} accounts and ${result.transactions_synced} transactions.`);
      invalidateAllData();
      await fetchAccounts();
    } catch (error) {
      console.error('Error syncing data:', error);
      setSyncMessage('Failed to sync data. Please try again.');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const getAccountIcon = (type: string) => {
    switch (type) {
      case 'credit':
        return <CreditCard />;
      case 'depository':
        return <Savings />;
      case 'loan':
        return <AccountBalanceWallet />;
      default:
        return <AccountBalance />;
    }
  };

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case 'credit':
        return 'error';
      case 'depository':
        return 'success';
      case 'loan':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatBalance = (balance: number | null | undefined) => {
    if (balance === null || balance === undefined || typeof balance !== 'number') return 'N/A';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(balance));
    return balance < 0 ? `-${formatted}` : formatted;
  };

  const getCategoryKey = (account: Account): string => {
    const type = account.type;
    const subtype = (account.subtype || '').toLowerCase();
    if (type === 'depository') {
      if (subtype.includes('saving')) return 'savings';
      return 'checking';
    }
    if (type === 'investment' || type === 'brokerage') return 'investments';
    if (type === 'credit') return 'credit';
    if (type === 'loan') return 'loans';
    return 'other';
  };

  const categoryMeta: Record<string, { label: string; icon: string; gradient: string; accent: string; iconBg: string; iconColor: string }> = {
    checking: { label: 'Checking', icon: '💰', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', accent: '#059669', iconBg: '#d1fae5', iconColor: '#059669' },
    savings: { label: 'Savings', icon: '🏦', gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', accent: '#0d9488', iconBg: '#ccfbf1', iconColor: '#0d9488' },
    investments: { label: 'Investments', icon: '📈', gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', accent: '#2563eb', iconBg: '#dbeafe', iconColor: '#2563eb' },
    credit: { label: 'Credit Cards', icon: '💳', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', accent: '#dc2626', iconBg: '#fee2e2', iconColor: '#dc2626' },
    loans: { label: 'Loans', icon: '🏛️', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', accent: '#d97706', iconBg: '#fef3c7', iconColor: '#b45309' },
    other: { label: 'Other', icon: '📦', gradient: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', accent: '#4b5563', iconBg: '#e5e7eb', iconColor: '#4b5563' },
  };

  const groupedAccounts = accounts.reduce<Record<string, Account[]>>((acc, account) => {
    const key = getCategoryKey(account);
    if (!acc[key]) acc[key] = [];
    acc[key].push(account);
    return acc;
  }, {});

  const categoryOrder = ['checking', 'savings', 'investments', 'credit', 'loans', 'other'].filter(k => groupedAccounts[k]?.length);

  const calculateCategoryTotal = (key: string): number => {
    const group = groupedAccounts[key] || [];
    return group.reduce((sum, a) => {
      const bal = typeof a.balances.current === 'number' ? a.balances.current : 0;
      if (key === 'credit' || key === 'loans') return sum - Math.abs(bal);
      return sum + bal;
    }, 0);
  };

  const filteredAccounts = selectedCategory === 'all'
    ? accounts
    : (groupedAccounts[selectedCategory] || []);

  const handleAccountClick = async (account: Account) => {
    setSelectedAccount(account);
    setDialogOpen(true);
    await fetchAccountTransactions(account.account_id);
  };

  const fetchAccountTransactions = async (accountId: string) => {
    try {
      setTransactionsLoading(true);
      const response = await cachedTransactions();
      // Filter transactions for the specific account
      const accountTransactions = response.transactions.filter(
        (transaction: Transaction) => transaction.account_id === accountId
      );
      
      // Sort transactions by date (newest first)
      const sortedTransactions = accountTransactions.sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      
      setAllTransactions(sortedTransactions);
      setAccountTransactions(sortedTransactions.slice(0, displayedTransactions));
      setDisplayedTransactions(20); // Reset to initial display count
    } catch (err: any) {
      console.error('Error fetching account transactions:', err);
      setAccountTransactions([]);
      setAllTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
    setAccountTransactions([]);
    setAllTransactions([]);
    setDisplayedTransactions(20);
  };

  const loadMoreTransactions = () => {
    setLoadingMore(true);
    const newDisplayCount = displayedTransactions + 20;
    setDisplayedTransactions(newDisplayCount);
    setAccountTransactions(allTransactions.slice(0, newDisplayCount));
    setLoadingMore(false);
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getAmountColor = (amount: number) => {
    return amount > 0 ? 'success.main' : 'error.main';
  };

  const getAmountColorForAccount = (transaction: Transaction) => {
    const accountType = getAccountType(transaction.account_id);
    const amount = getDisplayAmount(transaction);
    
    if (accountType === 'credit') {
      // For credit cards: positive = payment (good), negative = purchase (spending)
      return amount > 0 ? 'success.main' : 'error.main';
    } else {
      // For depository accounts: positive = income, negative = spending
      return amount > 0 ? 'success.main' : 'error.main';
    }
  };

  const getCategoryChips = (transaction: Transaction) => {
    const chips = [];
    
    // Handle new Plaid personal_finance_category format
    if (transaction.personal_finance_category) {
      const { primary, detailed } = transaction.personal_finance_category;
      
      // Primary category chip
      chips.push(
        <Chip
          key="primary"
          label={primary}
          size="small"
          variant="filled"
          color="primary"
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      );
      
      // Detailed category chip (if different from primary)
      if (detailed && detailed !== primary) {
        chips.push(
          <Chip
            key="detailed"
            label={detailed}
            size="small"
            variant="outlined"
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        );
      }
    }
    // Fallback to legacy category format
    else if (transaction.category && transaction.category.length > 0) {
      return transaction.category.slice(0, 2).map((category, index) => (
        <Chip
          key={index}
          label={category}
          size="small"
          variant="outlined"
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      ));
    }
    
    return chips.length > 0 ? chips : null;
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchAccounts}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" className="animate-fade-in">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 1
              }}
            >
              Your Accounts
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and view all your linked financial accounts
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={syncing ? <CircularProgress size={20} /> : <Sync />}
              onClick={handleSyncData}
              disabled={syncing}
              sx={{
                py: 1.5,
                px: 3,
                fontSize: '1rem',
                fontWeight: 600,
                borderWidth: 2,
                borderColor: '#6366f1',
                color: '#6366f1',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#4f46e5',
                  background: 'rgba(99, 102, 241, 0.08)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {syncing ? 'Syncing...' : 'Sync Data'}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddCircleOutline />}
              onClick={() => openPlaidLink()}
              disabled={!plaidReady || linkLoading}
              sx={{
                py: 1.5,
                px: 3,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  boxShadow: '0 6px 20px rgba(16, 185, 129, 0.5)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {linkLoading ? <CircularProgress size={24} color="inherit" /> : 'Link New Account'}
            </Button>
          </Box>
        </Box>

        {linkError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLinkError(null)}>
            {linkError}
          </Alert>
        )}

        {linkSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLinkSuccess(false)}>
            Account linked successfully! Your accounts list has been updated.
          </Alert>
        )}

        {syncMessage && (
          <Alert
            severity={syncMessage.includes('Failed') ? 'error' : 'success'}
            sx={{ mb: 2 }}
            onClose={() => setSyncMessage(null)}
          >
            {syncMessage}
          </Alert>
        )}
      </Box>

      {/* Summary Banners by Category */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${categoryOrder.length || 1}, minmax(0, 1fr))` }, gap: 2, mb: 4 }}>
        {categoryOrder.map((key) => {
          const meta = categoryMeta[key];
          const total = calculateCategoryTotal(key);
          const count = groupedAccounts[key].length;
          return (
            <Card
              key={key}
              onClick={() => setSelectedCategory(key)}
              sx={{
                cursor: 'pointer',
                background: meta.gradient,
                color: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                transform: selectedCategory === key ? 'translateY(-4px)' : 'none',
                boxShadow: selectedCategory === key
                  ? '0 12px 28px rgba(0,0,0,0.25)'
                  : '0 4px 12px rgba(0,0,0,0.12)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.25)',
                }
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: 11, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {meta.icon} {meta.label}
                </Typography>
                <Typography fontWeight={800} sx={{ mt: 0.5, fontSize: { md: 22, lg: 26 }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatBalance(total)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 11 }}>
                  {count} {count === 1 ? 'account' : 'accounts'}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Add Real Estate Property — Inline Expandable Panel */}
      <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {/* Toggle header */}
          <Box
            onClick={() => setShowAddProperty(!showAddProperty)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f8fafc' },
              transition: 'background-color 0.15s',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: showAddProperty ? '#6366f1' : '#ede9fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
              }}
            >
              <Home sx={{ fontSize: 20, color: showAddProperty ? 'white' : '#7c3aed' }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              Add Real Estate Property
            </Typography>
            {showAddProperty ? (
              <ExpandLess sx={{ color: '#94a3b8' }} />
            ) : (
              <ExpandMore sx={{ color: '#94a3b8' }} />
            )}
          </Box>

          {/* Expandable form */}
          <Collapse in={showAddProperty}>
            <Box sx={{ px: 3, pb: 3, pt: 1 }}>
              <Divider sx={{ mb: 2.5 }} />

              {/* Existing properties */}
              {manualAccounts.filter(a => a.type === 'real_estate').length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 1.5 }}>
                    Your Properties
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {manualAccounts.filter(a => a.type === 'real_estate').map(property => {
                      const isEditing = editingProperty?.id === property.id;
                      const isDeleting = deletingId === property.id;

                      if (isEditing) {
                        return (
                          <Box
                            key={property.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 2,
                              py: 1.5,
                              background: '#f0f0ff',
                              border: '2px solid',
                              borderColor: '#6366f1',
                              borderRadius: 2,
                            }}
                          >
                            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Home sx={{ fontSize: 18, color: '#7c3aed' }} />
                            </Box>
                            <TextField
                              size="small"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              label="Name"
                              sx={{ flex: '1 1 150px', minWidth: 120 }}
                            />
                            <TextField
                              size="small"
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              label="Value"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                              sx={{ flex: '1 1 140px', minWidth: 120 }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={handleSaveEdit}
                              disabled={savingEdit || !editName || !editValue}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                bgcolor: '#6366f1',
                                '&:hover': { bgcolor: '#4f46e5' },
                                minWidth: 60,
                              }}
                            >
                              {savingEdit ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => setEditingProperty(null)}
                              sx={{ textTransform: 'none', color: '#64748b', minWidth: 60 }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        );
                      }

                      return (
                        <Box
                          key={property.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            background: '#f8fafc',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            '&:hover .property-actions': { opacity: 1 },
                          }}
                        >
                          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Home sx={{ fontSize: 18, color: '#7c3aed' }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>{property.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatPropertyCurrency(property.current_balance)} · {property.subtype.replace(/_/g, ' ')}
                            </Typography>
                          </Box>
                          <Box
                            className="property-actions"
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              opacity: 0,
                              transition: 'opacity 0.15s',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleEditProperty(property)}
                              sx={{ color: '#64748b', '&:hover': { color: '#6366f1', bgcolor: '#ede9fe' } }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteProperty(property.id)}
                              disabled={isDeleting}
                              sx={{ color: '#64748b', '&:hover': { color: '#ef4444', bgcolor: '#fee2e2' } }}
                            >
                              {isDeleting ? <CircularProgress size={16} /> : <Delete sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Form fields in a row */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  label="Property Name"
                  placeholder="e.g. My House"
                  size="small"
                  value={propertyName}
                  onChange={(e) => setPropertyName(e.target.value)}
                  sx={{ flex: '1 1 180px', minWidth: 180 }}
                />
                <FormControl size="small" sx={{ flex: '1 1 180px', minWidth: 180 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={propertyType}
                    label="Type"
                    onChange={(e) => setPropertyType(e.target.value)}
                  >
                    <MenuItem value="primary_residence">Primary Residence</MenuItem>
                    <MenuItem value="investment_property">Investment Property</MenuItem>
                    <MenuItem value="rental_property">Rental Property</MenuItem>
                    <MenuItem value="vacation_home">Vacation Home</MenuItem>
                    <MenuItem value="land">Land</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Estimated Value"
                  placeholder="0.00"
                  size="small"
                  type="number"
                  value={propertyValue}
                  onChange={(e) => setPropertyValue(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{ flex: '1 1 160px', minWidth: 160 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddProperty}
                  disabled={!propertyName || !propertyValue || addingProperty}
                  startIcon={addingProperty ? <CircularProgress size={16} color="inherit" /> : <Add />}
                  sx={{
                    height: 40,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#6366f1',
                    '&:hover': { bgcolor: '#4f46e5' },
                    flexShrink: 0,
                  }}
                >
                  {addingProperty ? 'Adding...' : 'Add'}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Sidebar + Content Layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 3 }}>
        {/* Sidebar */}
        <Card sx={{ height: 'fit-content', border: '2px solid', borderColor: 'divider', position: { md: 'sticky' }, top: { md: 24 } }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ px: 1, fontSize: 13 }}>
              Filter by Type
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {(() => {
                const items = [
                  { key: 'all', label: 'All Accounts', icon: '📊', count: accounts.length },
                  ...categoryOrder.map(k => ({ key: k, label: categoryMeta[k].label, icon: categoryMeta[k].icon, count: groupedAccounts[k].length }))
                ];
                const renderRow = (item: typeof items[number], isChild: boolean) => {
                  const active = selectedCategory === item.key;
                  return (
                    <Box
                      key={item.key}
                      onClick={() => setSelectedCategory(item.key)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        mb: 0.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        background: active ? 'linear-gradient(90deg, rgba(99,102,241,0.12), rgba(139,92,246,0.12))' : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: active ? 'linear-gradient(90deg, rgba(99,102,241,0.18), rgba(139,92,246,0.18))' : 'rgba(0,0,0,0.04)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontSize: isChild ? 20 : 22 }}>{item.icon}</Typography>
                        <Typography
                          variant="body1"
                          fontWeight={active ? 700 : isChild ? 500 : 700}
                          color={active ? 'primary.main' : 'text.primary'}
                          sx={{ fontSize: isChild ? 14 : 15 }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Chip
                        label={item.count}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: 12,
                          fontWeight: 700,
                          px: 0.5,
                          background: active ? '#e0e7ff' : '#f3f4f6',
                          color: active ? '#6366f1' : '#6b7280',
                        }}
                      />
                    </Box>
                  );
                };
                return (
                  <>
                    {renderRow(items[0], false)}
                    <Box
                      sx={{
                        ml: 2.5,
                        pl: 2,
                        borderLeft: '2px solid',
                        borderColor: 'divider',
                        mt: 0.5,
                      }}
                    >
                      {items.slice(1).map(item => renderRow(item, true))}
                    </Box>
                  </>
                );
              })()}
            </Box>
          </CardContent>
        </Card>

        {/* Accounts Grid - 2 per row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, alignContent: 'flex-start' }}>
          {filteredAccounts.map((account) => (
            <Card
              key={account.account_id}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '2px solid',
                borderColor: 'divider',
                background: 'white',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 40px rgba(99, 102, 241, 0.2)',
                  borderColor: 'primary.main',
                }
              }}
              onClick={() => handleAccountClick(account)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  {getAccountIcon(account.type)}
                  <Box ml={1}>
                    <Typography variant="h6" noWrap>
                      {account.institution_name ? `${account.institution_name} ${account.name}` : account.name}
                    </Typography>
                    <Chip
                      label={account.subtype}
                      color={getAccountTypeColor(account.type)}
                      size="small"
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Account Type: {account.type}
                </Typography>

                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    {account.type === 'credit' ? 'Current Debt' : 'Current Balance'}
                  </Typography>
                  <Typography variant="h5" color={account.type === 'credit' ? 'error.main' : 'primary'}>
                    {account.type === 'credit' ?
                      formatBalance(Math.abs(account.balances.current || 0)) :
                      formatBalance(account.balances.current || 0)
                    }
                  </Typography>
                </Box>

                {account.balances.available !== null && (
                  <Box mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      {account.type === 'credit' ? 'Available Credit' : 'Available Balance'}
                    </Typography>
                    <Typography variant="body1" color={account.type === 'credit' ? 'success.main' : 'inherit'}>
                      {formatBalance(account.balances.available || 0)}
                    </Typography>
                  </Box>
                )}

                {account.balances.limit !== null && (
                  <Box mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Credit Limit
                    </Typography>
                    <Typography variant="body1">
                      {formatBalance(account.balances.limit || 0)}
                    </Typography>
                  </Box>
                )}
              </CardContent>
              <CardActions>
                <Button size="small" onClick={(e) => {
                  e.stopPropagation();
                  handleAccountClick(account);
                }}>
                  View Details
                </Button>
              </CardActions>
            </Card>
          ))}
          {filteredAccounts.length === 0 && accounts.length > 0 && (
            <Card sx={{ gridColumn: '1 / -1' }}>
              <CardContent>
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="textSecondary">
                    No accounts in this category.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {accounts.length === 0 && (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" gutterBottom>
                No accounts found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Make sure your account is properly linked and try refreshing.
              </Typography>
              <Button variant="contained" onClick={fetchAccounts} sx={{ mt: 2 }}>
                Refresh
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Account Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {selectedAccount && getAccountIcon(selectedAccount.type)}
              <Box ml={1}>
                <Typography variant="h6">
                  {selectedAccount?.institution_name ? `${selectedAccount.institution_name} ${selectedAccount.name}` : selectedAccount?.name}
                </Typography>
                <Chip
                  label={selectedAccount?.subtype}
                  color={selectedAccount ? getAccountTypeColor(selectedAccount.type) : 'default'}
                  size="small"
                />
              </Box>
            </Box>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedAccount && (
            <>
              {/* Account Summary */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Account Summary
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Current Balance
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {formatBalance(selectedAccount.balances.current || 0)}
                      </Typography>
                    </Box>
                    {selectedAccount.balances.available !== null && (
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Available Balance
                        </Typography>
                        <Typography variant="h6">
                          {formatBalance(selectedAccount.balances.available || 0)}
                        </Typography>
                      </Box>
                    )}
                    {selectedAccount.balances.limit !== null && (
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Credit Limit
                        </Typography>
                        <Typography variant="h6">
                          {formatBalance(selectedAccount.balances.limit || 0)}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Account Type
                      </Typography>
                      <Typography variant="h6">
                        {selectedAccount.type}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Divider sx={{ mb: 3 }} />

              {/* Transactions */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Recent Transactions ({accountTransactions.length} of {allTransactions.length})
                </Typography>
                {accountTransactions.length > 0 && (
                  <Typography variant="body2" color="textSecondary">
                    {accountTransactions.length > 0 && (
                      <>
                        From {formatDate(accountTransactions[accountTransactions.length - 1].date)} to {formatDate(accountTransactions[0].date)}
                      </>
                    )}
                  </Typography>
                )}
              </Box>

              {/* Transaction Summary */}
              {accountTransactions.length > 0 && (
                <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Total Spent
                        </Typography>
                        <Typography variant="h6" color="error">
                          {formatAmount(
                            accountTransactions
                              .filter(t => getDisplayAmount(t) < 0)
                              .reduce((total, t) => total + Math.abs(getDisplayAmount(t)), 0)
                          )}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Total Income
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {formatAmount(
                            accountTransactions
                              .filter(t => getDisplayAmount(t) > 0)
                              .reduce((total, t) => total + getDisplayAmount(t), 0)
                          )}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Net Amount
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {formatAmount(
                            accountTransactions.reduce((total, t) => total + getDisplayAmount(t), 0)
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
              
              {transactionsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : accountTransactions.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accountTransactions.map((transaction) => (
                        <TableRow key={transaction.transaction_id}>
                          <TableCell>
                            {formatDate(transaction.date)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {transaction.name}
                            </Typography>
                            {transaction.merchant_name && (
                              <Typography variant="caption" color="textSecondary">
                                {transaction.merchant_name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box>
                              {getCategoryChips(transaction)}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color={getAmountColorForAccount(transaction)}
                            >
                              {formatAmount(getDisplayAmount(transaction))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="textSecondary">
                    No transactions found for this account
                  </Typography>
                </Box>
              )}

              {/* Load More Buttons */}
              {accountTransactions.length > 0 && accountTransactions.length < allTransactions.length && (
                <Box display="flex" justifyContent="center" gap={2} mt={3}>
                  <Button
                    variant="outlined"
                    onClick={loadMoreTransactions}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                  >
                    {loadingMore ? 'Loading...' : `Load More (${allTransactions.length - accountTransactions.length} remaining)`}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setAccountTransactions(allTransactions);
                      setDisplayedTransactions(allTransactions.length);
                    }}
                    disabled={loadingMore}
                  >
                    Load All ({allTransactions.length} total)
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Accounts;
